import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, watch } from 'vue'
import { storeToRefs } from 'pinia'

import MapRoute from '@/components/core/map/MapRoute.vue'
import { mapSymbol } from '@/components/core/map/utils'
import { useTransportationStore } from '@/stores/transportation'
import { RouteSuggestion, Transportation } from '@/types/transportation'
import { decodePolyline } from '@/utils/utility-manager'
import { useSelectedTrip } from '@/composables/trip'
import {
  createDirectRoute,
  getDefaultEmojiForTransportationType,
} from '@/utils/transportation'
import { locationToAutocompleteResult } from '@/utils/location'
import { useLocalTripSettings } from '@/composables/trip-settings'


export default /*#__PURE__*/_defineComponent({
  __name: 'MapRoutes',
  props: {
    fitToBoundsCallback: null,
    boundsPadding: null
  },
  setup(__props: any) {

const props = __props as {
  fitToBoundsCallback?: () => void
  boundsPadding?: google.maps.Padding
};

const MAP_BOUNDS_PADDING = { left: 48, right: 48, top: 48, bottom: 48 }



const map = inject(mapSymbol)
const trip = useSelectedTrip()

const transportationStore = useTransportationStore()
const {
  allTransportations,
  availableRoutes,
  showAlternativeRoutes,
  selectedTransportation,
} = storeToRefs(transportationStore)

const { tripSettings } = useLocalTripSettings(trip.value._id)

const transportation = computed(() => {
  return selectedTransportation.value?.transportation
})

function isCurrentRoute(routePolyline: string) {
  return transportation.value?.route?.polyline === routePolyline
}

function shouldHideAlternativeRoute(routePolyline: string) {
  return !showAlternativeRoutes.value && !isCurrentRoute(routePolyline)
}

function isSelectedTransportation(transportationId: string) {
  return (
    transportation.value !== undefined &&
    '_id' in transportation.value &&
    transportation.value._id === transportationId
  )
}

function shouldHideTransportation(t: Transportation) {
  return (
    !tripSettings.value.mapSettings.showRoutes ||
    isSelectedTransportation(t._id)
  )
}

function shouldHidePlaceholderRoute(waypointId: string) {
  return (
    !tripSettings.value.mapSettings.showRoutes ||
    waypointId === selectedTransportation.value?.waypointId
  )
}

function calculateBoundsForRoute(
  route: Pick<RouteSuggestion, 'center' | 'polyline' | 'locations'>,
) {
  const bounds = new google.maps.LatLngBounds()
  bounds.extend(
    new google.maps.LatLng(route.center.latitude, route.center.longitude),
  )
  const decodedPolyline = decodePolyline(route.polyline)
  decodedPolyline.forEach((point) => {
    bounds.extend(new google.maps.LatLng(point.lat, point.lng))
  })

  route.locations.forEach((location) => {
    bounds.extend(new google.maps.LatLng(location.latitude, location.longitude))
  })

  return bounds
}

watch(
  [availableRoutes, selectedTransportation],
  (
    [newAvailableRoutes, newSelectedTransportation],
    [_, oldSelectedTransportation],
  ) => {
    if (
      (!oldSelectedTransportation && !newSelectedTransportation) ||
      !map?.value
    ) {
      return
    }

    if (!newAvailableRoutes.length || !newSelectedTransportation) {
      props.fitToBoundsCallback?.()
    } else {
      const newBounds = new google.maps.LatLngBounds()

      availableRoutes.value.forEach((route) => {
        const routeBounds = calculateBoundsForRoute(route)
        newBounds.union(routeBounds)
      })

      map.value.fitBounds(newBounds, props.boundsPadding ?? MAP_BOUNDS_PADDING)
    }
  },
  { immediate: true },
)

function onMarkerClick(params: {
  transportation: Transportation
  waypointId: string
}) {
  if (params.transportation.type === 'flight') {
    transportationStore.selectTransportation({
      transportation: {
        departureAirport: { iata: '' },
        arrivalAirport: { iata: '' },
        ...params.transportation,
        origin: locationToAutocompleteResult(params.transportation.origin),
        destination: locationToAutocompleteResult(
          params.transportation.destination,
        ),
      },
      waypointId: params.waypointId,
    })
  } else {
    transportationStore.selectTransportation({
      transportation: {
        ...params.transportation,
        origin: locationToAutocompleteResult(params.transportation.origin),
        destination: locationToAutocompleteResult(
          params.transportation.destination,
        ),
      },
      waypointId: params.waypointId,
    })
  }
}

// Placeholder routes ----------------------------------------------------------

/**
 * Create direct routes between waypoints that do not have atleas one
 * transportation with a route.
 */
const placeholderRoutes = computed(() => {
  const routes = new Map<string, RouteSuggestion>()
  for (let index = 0; index < trip.value.waypoints.length - 1; index++) {
    const waypointTransportations = allTransportations.value.get(
      trip.value.waypoints[index]._id,
    )
    if (
      waypointTransportations &&
      waypointTransportations.some((transportation) => transportation.route)
    ) {
      continue
    }

    routes.set(
      trip.value.waypoints[index]._id,
      createDirectRoute(
        trip.value.waypoints[index].location,
        trip.value.waypoints[index + 1].location,
      ),
    )
  }

  return routes
})

function onPlaceholderRouteClick(waypointId: string) {
  transportationStore.selectTransportation({
    transportation: {
      type: 'driving',
      documents: [],
      rentals: [],
      emoji: getDefaultEmojiForTransportationType('driving'),
    },
    waypointId,
  })
}

return { __sfc: true,MAP_BOUNDS_PADDING, props, map, trip, transportationStore, allTransportations, availableRoutes, showAlternativeRoutes, selectedTransportation, tripSettings, transportation, isCurrentRoute, shouldHideAlternativeRoute, isSelectedTransportation, shouldHideTransportation, shouldHidePlaceholderRoute, calculateBoundsForRoute, onMarkerClick, placeholderRoutes, onPlaceholderRouteClick, MapRoute, decodePolyline }
}

})